import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, Volume2, VolumeX, Loader2, Radio } from "lucide-react";
import { transcribeAudio } from "../lib/api";

const SILENCE_RMS = 0.012;
const SILENCE_HOLD_MS = 2200;
const MIN_SPEECH_MS = 600;
const MAX_TAKE_MS = 120000;

/**
 * Speak-your-answer control. In hands-free mode it arms itself after each
 * Cartographer turn and stops on its own once you go quiet.
 */
export const VoiceControls = ({
  onTranscript,
  speakOut,
  setSpeakOut,
  handsFree,
  setHandsFree,
  armSignal,
  disabled,
}) => {
  const [recording, setRecording] = useState(false);
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const rec = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const raf = useRef(null);
  const ctx = useRef(null);

  const teardownMeter = useCallback(() => {
    cancelAnimationFrame(raf.current);
    ctx.current?.close?.();
    ctx.current = null;
    setLevel(0);
  }, []);

  useEffect(
    () => () => {
      clearInterval(timer.current);
      teardownMeter();
    },
    [teardownMeter]
  );

  const stop = useCallback(() => {
    if (rec.current?.state === "recording") rec.current.stop();
  }, []);

  const start = useCallback(
    async (auto = false) => {
      if (rec.current?.state === "recording") return;
      setErr("");
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
        chunks.current = [];
        mr.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
        mr.onstop = async () => {
          stream.getTracks().forEach((t) => t.stop());
          clearInterval(timer.current);
          teardownMeter();
          setRecording(false);
          setSeconds(0);
          const blob = new Blob(chunks.current, { type: "audio/webm" });
          if (blob.size < 1200) {
            setErr(auto ? "nothing heard — tap the mic" : "Too short — hold the mic and speak.");
            return;
          }
          setWorking(true);
          try {
            const text = await transcribeAudio(blob);
            if (text) onTranscript(text);
            else setErr("Nothing heard.");
          } catch {
            setErr("Transcription failed.");
          } finally {
            setWorking(false);
          }
        };
        rec.current = mr;
        mr.start();
        setRecording(true);
        timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);

        // voice-activity detection so hands-free can end the take by itself
        const ac = new (window.AudioContext || window.webkitAudioContext)();
        ctx.current = ac;
        const src = ac.createMediaStreamSource(stream);
        const analyser = ac.createAnalyser();
        analyser.fftSize = 1024;
        src.connect(analyser);
        const buf = new Float32Array(analyser.fftSize);
        const began = performance.now();
        let spokeAt = 0;
        let quietSince = 0;

        const watch = () => {
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
          const rms = Math.sqrt(sum / buf.length);
          setLevel(Math.min(1, rms * 14));
          const now = performance.now();

          if (rms > SILENCE_RMS) {
            if (!spokeAt) spokeAt = now;
            quietSince = 0;
          } else if (spokeAt && now - spokeAt > MIN_SPEECH_MS) {
            if (!quietSince) quietSince = now;
            else if (now - quietSince > SILENCE_HOLD_MS) {
              stop();
              return;
            }
          }
          if (now - began > MAX_TAKE_MS) {
            stop();
            return;
          }
          raf.current = requestAnimationFrame(watch);
        };
        raf.current = requestAnimationFrame(watch);
      } catch {
        setErr("Microphone unavailable.");
        setHandsFree?.(false);
      }
    },
    [onTranscript, stop, teardownMeter, setHandsFree]
  );

  // hands-free: the parent bumps armSignal when the Cartographer has finished talking
  useEffect(() => {
    if (!handsFree || !armSignal || disabled || working) return;
    const t = setTimeout(() => start(true), 450);
    return () => clearTimeout(t);
  }, [armSignal, handsFree, disabled, working]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleHandsFree = () => {
    const next = !handsFree;
    setHandsFree(next);
    if (next) {
      setSpeakOut(true);
      start(true);
    } else {
      stop();
    }
  };

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        data-testid={recording ? "voice-stop-btn" : "voice-record-btn"}
        onClick={() => (recording ? stop() : start(false))}
        disabled={disabled || working}
        title={recording ? "Stop and transcribe" : "Speak your answer"}
        className={`flex h-9 w-9 items-center justify-center border transition-colors ${
          recording
            ? "border-zinc-100 bg-zinc-100 text-[var(--void)]"
            : "border-[var(--line-hi)] text-zinc-400 hover:border-zinc-100 hover:text-zinc-100"
        } disabled:opacity-30`}
      >
        {working ? (
          <Loader2 size={13} className="animate-spin" />
        ) : recording ? (
          <Square size={11} strokeWidth={2} />
        ) : (
          <Mic size={13} strokeWidth={1.6} />
        )}
      </button>

      <button
        type="button"
        data-testid="voice-speak-toggle"
        onClick={() => setSpeakOut((v) => !v)}
        title={speakOut ? "Cartographer speaks: on" : "Cartographer speaks: off"}
        className={`flex h-9 w-9 items-center justify-center border transition-colors ${
          speakOut
            ? "border-zinc-100 text-zinc-100"
            : "border-[var(--line-hi)] text-zinc-600 hover:text-zinc-300"
        }`}
      >
        {speakOut ? <Volume2 size={13} strokeWidth={1.6} /> : <VolumeX size={13} strokeWidth={1.6} />}
      </button>

      <button
        type="button"
        data-testid="hands-free-toggle"
        onClick={toggleHandsFree}
        title="Hands free — it listens, you just talk"
        className={`flex h-9 items-center gap-2 border px-3 font-data text-[10px] uppercase tracking-[0.16em] transition-colors ${
          handsFree
            ? "border-zinc-100 bg-zinc-100 text-[var(--void)]"
            : "border-[var(--line-hi)] text-zinc-500 hover:text-zinc-200"
        }`}
      >
        <Radio size={12} strokeWidth={1.6} className={handsFree ? "animate-pulse" : ""} />
        Hands free
      </button>

      {recording && (
        <div className="flex h-5 items-end gap-[2px]" data-testid="voice-level-meter">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <span
              key={i}
              className="w-[2px] bg-zinc-200 transition-all duration-100"
              style={{ height: `${Math.max(2, Math.min(1, level * (1 + i * 0.12)) * 18)}px` }}
            />
          ))}
        </div>
      )}

      <span className="label" data-testid="voice-status">
        {recording
          ? `${handsFree ? "listening" : "recording"} ${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`
          : working
            ? "transcribing"
            : err || (handsFree ? "hands free armed" : "voice ready")}
      </span>
    </div>
  );
};

export default VoiceControls;
